import { useEffect } from 'react';

import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/shadcn/combobox';
import { Field, FieldLabel } from '@/components/shadcn/field';
import useAnvaTaskReasons from '@/features/crm/queries/hooks/useAnvaTaskReasons';

const DEFAULT_TASK_REASON_CODE = '00009';

type Reason = { label: string; value: string };

type Props<T extends FieldValues> = {
  control: Control<T>;
  name?: Path<T>;
};

export default function AnvaReasonField<T extends FieldValues>({
  control,
  name,
}: Props<T>) {
  const { t } = useTranslation();

  const { data: reasons } = useAnvaTaskReasons();

  const { field } = useController({
    control,
    name: name ?? ('reason' as Path<T>),
  });

  useEffect(() => {
    if (field.value) return;

    const defaultReason = reasons?.find(
      (reason) => reason.value === DEFAULT_TASK_REASON_CODE,
    );
    if (defaultReason) field.onChange(defaultReason.value);
  }, [reasons, field]);

  return (
    <Field>
      <FieldLabel htmlFor="reason">{t('TASK_FIELD_REASON')}</FieldLabel>

      <Combobox
        items={reasons ?? []}
        value={reasons?.find((item) => item.value === field.value) ?? null}
        onValueChange={(item) => field.onChange(item?.value)}
        name={field.name}
      >
        <ComboboxInput placeholder={t('TASK_FIELD_REASON_PLACEHOLDER')} />

        <ComboboxContent>
          <ComboboxList>
            {(item: Reason) => (
              <ComboboxItem key={item.value} value={item}>
                {item.label}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </Field>
  );
}
